import { useState } from 'react';
import { User, UseLinks, Link, LinkFields, AppOption } from '../../types';
import * as parseHelpers from '../../helpers/parseLink';
import { getLinks, createLink, updateLink, removeLink, getTracker } from '../../helpers/api';
import { formatTrackerOption } from '../useApps';
import { createStorage } from './storage';
import { formatTrackerToken } from 'src/helpers/formatTrackerToken';
import { formatLink } from 'src/helpers/formatLink';

interface UseLinksProps {
  fetchApp: (token: string) => Promise<AppOption | void>;
}

export const useLinks = ({ fetchApp }: UseLinksProps): UseLinks => {
  const [list, setList] = useState<Link[]>([]);
  const [editFields, setEditFields] = useState<LinkFields | undefined>();
  const [highlightedId, setHighlightedId] = useState<number>();
  const [processingId, setProcessingId] = useState<number>();
  const [isInvalid, setIsInvalid] = useState<boolean[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isArchivedVisible, setIsArchivedVisible] = useState(false);
  const toggleIsArchivedVisible = () => setIsArchivedVisible(visible => !visible);

  const fetch = async () => {
    const links = await getLinks();
    setList(links);
    return links;
  };

  const migrateData = async (user: User) => {
    const storage = createStorage(`${user.id}-${user.current_account_id}`);
    const storageList = storage.getList();

    if (storageList.length > 0) {
      const migratedLinks = await fetch();
      const migragedUrls = migratedLinks.map(link => link.url);

      for (let index = 0; index < storageList.length; index++) {
        const { legacyIndex, ...link } = storageList[index];

        // do not migrate duplicated links
        if (migragedUrls.includes(link.url)) {
          storage.markAsMigrated(legacyIndex);
          continue;
        }

        await createLink(link);
        storage.markAsMigrated(legacyIndex);
        migragedUrls.push(link.url);
      }
    }

    const links = await fetch();
    validateLinks(links);
  };

  const validateLinks = async (links: Link[]) => {
    for (let index = 0; index < links.length; index++) {
      const link = links[index];
      setProcessingId(link.id);

      if (!link.verification_required) {
        continue;
      }

      let url = link.url;
      try {
        const { fields, trackersApps } = await parseLink(link, fetchApp);
        const [target, android] = trackersApps;
        const { app } = target;

        if (fields.dataResidency !== app.dataResidency) {
          const { trackerToken } = formatTrackerToken([
            [fields.targetTracker, fields.targetTrackerManual, app],
            [fields.androidTracker, fields.androidTrackerManual, android?.app],
          ]);

          let { rawUniversalLink } = fields;
          if (rawUniversalLink) {
            const ulinkParsed = parseHelpers.parseRawUlink(rawUniversalLink);
            if (ulinkParsed) {
              rawUniversalLink = [ulinkParsed.prefix, app.dataResidency, ulinkParsed.postfix].filter(Boolean).join('.');
            }
          }

          url = formatLink({
            ...fields,
            trackerToken,
            rawUniversalLink,
            dataResidency: app.dataResidency,
            additionalParameters: target.tracker?.additionalParameters,
            androidAppSchemePrefilled: !android?.app?.androidAppScheme,
          });
        }
      } catch {
        // Link is not valid anymore. Mark that it doesn't require verification in the future.
      }

      const updatedLink = await updateLink(link.id, {
        url,
        verification_required: false,
      });
      setList(_list => _list.map(item => (item.id === updatedLink.id ? { ...updatedLink } : item)));
    }

    setProcessingId(undefined);
  };

  const create = async (link: Omit<Link, 'id'>) => {
    setIsLoading(true);
    const response = await createLink(link);
    setList(_list => [response, ..._list]);
    setIsLoading(false);
    setHighlightedId(response.id);
  };

  const remove = async (id: number) => {
    setIsLoading(true);
    await removeLink(id);
    setList(_list => _list.filter(item => item.id !== id));
    setIsLoading(false);
    setHighlightedId(undefined);
  };

  const update = async (id: number, updates: Partial<Link> & { achived?: boolean }) => {
    setIsLoading(true);
    const link = await updateLink(id, updates);
    setList(_list => _list.map(item => (item.id === id ? link : item)));
    setIsLoading(false);
  };

  const edit = async (id: number) => {
    setHighlightedId(undefined);

    const link = list.find(item => item.id === id);
    if (!link) return;

    try {
      setIsLoading(true);
      const { fields } = await parseLink(link, fetchApp);
      setEditFields({ ...fields, id });
    } catch (e) {
      console.warn(e);

      setIsInvalid(invalidList => {
        invalidList = [...invalidList];
        invalidList[id] = true;
        return invalidList;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const skipEdit = () => setEditFields(undefined);

  return {
    fetch,
    migrateData,
    list,
    create,
    update: async (id, link) => {
      await update(id, link);
      skipEdit();
      setHighlightedId(id);
    },
    highlightedId,
    processingId,
    remove,
    edit,
    editFields,
    skipEdit,
    isInvalid,
    isLoading,
    isArchivedVisible,
    toggleIsArchivedVisible,
  };
};

const parseLink = async (link: Omit<Link, 'id'>, fetchApp: (token: string) => Promise<AppOption | void>) => {
  const { trackerToken, ...commonFields } = parseHelpers.parseCommonFields(link.url);
  if (!trackerToken) throw new Error('Missing tracker token');

  const trackersTokens = trackerToken?.split('_');
  let trackersApps;
  try {
    trackersApps = await Promise.all(
      trackersTokens.map(async token => {
        const tracker = await getTracker(token);
        const app = await fetchApp(tracker.app_token);
        if (!app) throw new Error('App is not available');
        return { tracker: formatTrackerOption(tracker), app };
      }),
    );
  } catch {
    throw new Error('Can not find tracker/app by tracker token');
  }

  const fields = parseHelpers.parseFields(
    {
      ...commonFields,
      adEnvironment: link.ad_environment,
    },
    trackersApps,
  );

  return {
    trackersApps,
    fields,
  };
};
